import type { ChatMessageSummary } from '@fetmode/types';
import { useCallback, useEffect, useRef } from 'react';

import { playJoinSound, playTipSound, unlockLiveRoomSounds } from './live-room-sounds.js';

export function useLiveRoomSounds(userId: string | undefined): {
  notifyChatMessages: (messages: ChatMessageSummary[], isInitialLoad: boolean) => void;
  notifySelfJoined: () => void;
  notifySelfTip: () => void;
  resetChatSoundBaseline: () => void;
} {
  const seenChatIdsRef = useRef(new Set<string>());

  useEffect(() => {
    const unlock = (): void => {
      unlockLiveRoomSounds();
    };

    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });

    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  const resetChatSoundBaseline = useCallback((): void => {
    seenChatIdsRef.current = new Set();
  }, []);

  const notifyChatMessages = useCallback(
    (messages: ChatMessageSummary[], isInitialLoad: boolean): void => {
      for (const message of messages) {
        if (seenChatIdsRef.current.has(message.id)) {
          continue;
        }

        seenChatIdsRef.current.add(message.id);

        if (isInitialLoad) {
          continue;
        }

        if (message.kind === 'TIP' && message.userId !== userId) {
          playTipSound();
          continue;
        }

        if (message.kind === 'ENTRANCE' && message.userId !== userId) {
          playJoinSound({ soft: true });
        }
      }
    },
    [userId],
  );

  const notifySelfJoined = useCallback((): void => {
    playJoinSound();
  }, []);

  const notifySelfTip = useCallback((): void => {
    playTipSound();
  }, []);

  return {
    notifyChatMessages,
    notifySelfJoined,
    notifySelfTip,
    resetChatSoundBaseline,
  };
}
